<template>
  <div>
    <div
      v-if="showSearchInput && !isMobile()"
      class="popup-wrap popup-lobby"
      :class="{ active: showSearchInput }"
    >
      <div class="popup-box">
        <div class="content">
          <div class="top">
            <div class="title">
              <h3>{{ $t("Bao.search_game") }}</h3>
              <a class="popup-close" @click="hide"><i class="fa-solid fa-xmark" /></a>
            </div>
            <div class="search-input">
              <input v-model="searchText" type="text" :placeholder="$t('Bao.find_game')">
            </div>
            <div class="trending-tags">
              <button
                v-for="(keyWord, key) in $store.getters.searchKeyWord.game"
                :key="key"
                @click="setSearchText(keyWord)"
              >
                <span>{{ keyWord }}</span>
              </button>
            </div>
          </div>
          <div class="search-result">
            <div
              v-for="item in limitedResult"
              :key="item.id"
              class="search-game-info"
              :class="{ active: item.id === selectedGame.id }"
            >
              <div class="search-game-text" @click="apply(item)">
                <i>
                  <picture><img :src="item.image" loading="lazy" alt="Images of Search Games"></picture>
                </i>
                <span>{{ item.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 手機版：由 tab bar 放大鏡開啟的底部面板 -->
    <template v-if="isMobile()">
      <div
        ref="sheet"
        class="lobby-search-bar shared-game-search"
        :class="{ 'is-dragging': dragStartY !== null, 'is-compact': isCompact }"
        :style="sheetStyle"
        :role="showSearchInput ? 'dialog' : null"
        :aria-modal="showSearchInput ? 'true' : null"
        :aria-labelledby="showSearchInput ? 'mobile-search-heading' : null"
        @keydown.esc="hide"
      >
        <ul>
          <li class="search-box">
            <i class="fa-solid fa-magnifying-glass iconSearch" />
            <input
              ref="searchInput"
              v-model="searchText"
              class="input-space"
              type="text"
              autocomplete="off"
              enterkeyhint="search"
              aria-controls="mobile-game-search-results"
              :aria-label="$t('Bao.find_game')"
              :placeholder="$t('Bao.find_game')"
              @keydown.enter="$event.target.blur()"
              @focus="updateViewport"
              @blur="onSearchInputBlur"
            >
            <button
              v-show="searchText"
              type="button"
              class="mobile-search-clear"
              @click="clearSearchText"
            >
              ×
            </button>
          </li>
        </ul>
        <!-- 手機面板不顯示關鍵字 chips（切版 10/02 移除），桌機彈窗保留 -->
        <button
          type="button"
          class="mobile-search-grabber"
          :aria-label="$t('Bao.close')"
          @click="onGrabberClick"
          @pointerdown="onDragStart"
          @pointermove="onDragMove"
          @pointerup="onDragEnd"
          @pointercancel="onDragEnd"
        />
        <section class="mobile-search-content">
          <div class="mobile-search-heading">
            <h2 id="mobile-search-heading" ref="searchHeading" tabindex="-1">
              {{ $t('Bao.search_game') }}
            </h2>
            <button type="button" class="mobile-search-close" :aria-label="$t('Bao.close')" @click="hide">
              ×
            </button>
          </div>
          <div id="mobile-game-search-results" ref="results" class="search-result">
            <button
              v-for="item in limitedResult"
              :key="item.id"
              type="button"
              class="search-game-info"
              :class="{ active: item.id === selectedGame.id }"
              @click="apply(item)"
            >
              <span class="search-game-text">
                <img :src="item.image" loading="lazy" alt="">
                <span>{{ item.name }}</span>
              </span>
            </button>
          </div>
          <p v-show="limitedResult.length === 0" class="mobile-search-empty">
            {{ $t('Bao.search_results_are_empty') }}
          </p>
        </section>
      </div>
      <div class="mobile-search-backdrop" aria-hidden="true" @click="hide" />
    </template>
  </div>
</template>

<script>
import { launchGameWithProviderInfo } from '@/utils/games'
import { getGameList } from '@/api/game'
import { mapGetters } from 'vuex'
import { detectMob } from '@/utils/tool'

export default {
  name: 'SearchGame',
  props: {
    allGame: {
      type: Array,
      default: () => { return [] }
    }
  },
  data() {
    return {
      showSearch: false,
      searchText: '',
      debouncedSearchText: '',
      debounceTimer: null,
      selectedGame: {},
      gamePlayHistory: [],
      dragStartY: null,
      dragY: 0,
      suppressGrabberClickUntil: 0,
      isCompact: false,
      visibleHeight: null,
      visibleTop: 0,
      keyboardOffset: 0
    }
  },
  computed: {
    ...mapGetters(['gameIdsArray']),
    gameIdSet() {
      return new Set(this.gameIdsArray.map(gameObj => gameObj.game_id))
    },
    gameIndexMap() {
      return new Map(this.gameIdsArray.map((gameObj, index) => [gameObj.game_id, index]))
    },
    // 預先把各語系遊戲名稱串成小寫索引，只在 allGame 變動時重算一次，避免每個按鍵都重跑 toLowerCase
    searchableGames() {
      return this.allGame.map((game) => {
        let names = ''
        if (game && game.multiple && typeof game.multiple.name === 'object' && game.multiple.name !== null) {
          names = Object.values(game.multiple.name)
            .filter(gameName => typeof gameName === 'string')
            .join('\n')
            .toLowerCase()
        }
        return { game, names }
      })
    },
    filterResult() {
      if (this.debouncedSearchText === '') { // 沒有輸入關鍵字，顯示全部遊戲
        if (this.gameIdSet.size === 0) { // 沒有遊玩紀錄，顯示全部遊戲
          return this.allGame
        } else { // 顯示遊玩紀錄
          return this.gamePlayHistory // gamelist API資料
        }
      } else { // 輸入關鍵字
        const searchTextLowerCase = this.debouncedSearchText.toLowerCase()
        return this.searchableGames
          .filter(({ names }) => names.includes(searchTextLowerCase))
          .map(({ game }) => game)
      }
    },
    // 限制渲染筆數，避免單一關鍵字一次塞數百個節點造成卡頓
    limitedResult() {
      return this.filterResult.slice(0, this.debouncedSearchText === '' ? 40 : 60)
    },
    showSearchInput() {
      return this.$store.state.brand.showSearch
    },
    sheetStyle() {
      const style = {}
      if (this.dragY) style['--sheet-drag-y'] = `${this.dragY}px`
      if (this.visibleHeight) style['--search-visible-height'] = `${this.visibleHeight}px`
      if (this.visibleTop) style['--search-visible-top'] = `${this.visibleTop}px`
      if (this.keyboardOffset) style['--search-keyboard-offset'] = `${this.keyboardOffset}px`
      return style
    }
  },
  watch: {
    searchText(newVal) {
      clearTimeout(this.debounceTimer)
      if (newVal === '') { // 清空時立即還原，不需等待
        this.debouncedSearchText = ''
        return
      }
      this.debounceTimer = setTimeout(() => {
        this.debouncedSearchText = newVal
      }, 250)
    },
    showSearchInput(newVal) {
      if (!newVal) {
        this.searchText = ''
      } else {
        // 開啟時焦點放在標題，不主動叫出鍵盤；點輸入框後面板才切換為 is-compact
        this.$nextTick(() => {
          if (this.$refs.results) this.$refs.results.scrollTop = 0
          if (this.$refs.searchHeading) this.$refs.searchHeading.focus({ preventScroll: true })
        })
      }

      this.selectedGame = {}
      this.syncMobileSheet()
    },
    '$store.state.app.gameIdsArray'(to) {
      if (to && to.length) {
        this.fetchGameList()
      }
    }
  },
  created() {
    this.fetchGameList()
  },
  mounted() {
    if (!this.isMobile()) return
    this.syncMobileSheet()
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateViewport)
      window.visualViewport.addEventListener('scroll', this.updateViewport)
    }
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer)
    if (!this.isMobile()) return
    document.body.classList.remove('mobile-search-open')
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateViewport)
      window.visualViewport.removeEventListener('scroll', this.updateViewport)
    }
  },
  methods: {
    filterAndSortGames() {
      const filteredGames = this.allGame.filter(game => this.gameIdSet.has(game.id))
      return filteredGames.sort((a, b) => this.gameIndexMap.get(b.id) - this.gameIndexMap.get(a.id))
    },
    fetchGameList() {
      const gamesArr = Array.from(this.gameIdSet)
      if (gamesArr.length === 0) {
        return
      }

      getGameList({ game_id_list_string: JSON.stringify(gamesArr) })
        .then((response) => {
          this.gamePlayHistory = response.data.data
          this.$store.commit('SET_GAME_RECORD', this.gamePlayHistory)
        })
        .catch((err) => {
          console.log(err)
        })
    },
    apply(item) {
      launchGameWithProviderInfo(item)
      this.showSearch = false
      this.$store.commit('SET_SHOW_RESEARCH', false)
      this.searchText = ''
      this.selectedGame = {}
    },
    setSearchText(keyWord) {
      this.searchText = keyWord
    },
    hide() {
      this.showSearch = false
      this.$store.commit('SET_SHOW_RESEARCH', false)
      this.selectedGame = {}
      this.searchText = ''
    },
    clearSearchText() {
      this.searchText = ''
      if (this.$refs.searchInput) this.$refs.searchInput.focus()
    },
    // 手機面板開關由 body class 控制（樣式見 refresh/shared-game-search.css）
    syncMobileSheet() {
      if (!this.isMobile()) return
      document.body.classList.toggle('mobile-search-open', !!this.showSearchInput)
      this.updateViewport()
    },
    // 軟鍵盤開啟時依可視範圍（visualViewport 的 offsetTop + height）定位面板；輸入中改為 is-compact
    updateViewport() {
      const visual = window.visualViewport
      const height = visual ? visual.height : window.innerHeight
      this.visibleHeight = height
      this.visibleTop = visual ? visual.offsetTop : 0
      this.keyboardOffset = visual ? Math.max(0, window.innerHeight - height - visual.offsetTop) : 0
      this.isCompact = height < 500 || (!!this.$refs.searchInput && document.activeElement === this.$refs.searchInput)
    },
    // blur 當下 activeElement 尚未更新，下一幀再判斷
    onSearchInputBlur() {
      requestAnimationFrame(this.updateViewport)
    },
    onGrabberClick() {
      if (performance.now() >= this.suppressGrabberClickUntil) this.hide()
    },
    onDragStart(event) {
      if (event.button !== 0) return
      this.dragStartY = event.clientY
      this.dragY = 0
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    onDragMove(event) {
      if (this.dragStartY === null) return
      this.dragY = Math.max(0, event.clientY - this.dragStartY)
    },
    // 往下拖超過 80px 關閉面板
    onDragEnd(event) {
      if (this.dragStartY === null) return
      const dismiss = event.type === 'pointerup' && this.dragY > 80
      if (this.dragY > 8) this.suppressGrabberClickUntil = performance.now() + 300
      this.dragStartY = null
      this.dragY = 0
      if (dismiss) this.hide()
    },
    isMobile() {
      return detectMob()
    },
    smoothBehavior(refName) {
      this.$nextTick(() => {
        this.$refs[refName].scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
  }
}
</script>

<style scoped>
</style>
